<template>
    <div id="config-footprints">
        <FilterView placeholder="Chercher une emprise..." v-model="searchTerm">
            <CustomButton
                v-if="selected.length"
                icon="trash-alt"
                class="default red-theme"
                @click="showConfirmMany = true"
            >
                Supprimer
            </CustomButton>
            <CustomButton icon="plus" class="default green-theme" @click="handleNew">
                Ajouter une emprise
            </CustomButton>
        </FilterView>
        <ModalCalculateLoading v-if="loadingModalMessage" v-model="loadingModalMessage" />
        <ModalFootprint
            v-if="showEdit"
            v-model="footprint"
            :users="users"
            @dismiss="showEdit = false"
            @close="handleClose"
        />
        <ModalMessage
            v-if="messageModalMessage"
            v-model="messageModalMessage"
            v-bind:header="headerModalMessage"
            v-bind:body="bodyModalMessage"
            v-bind:error="errorModalMessage"
            v-bind:footer="footerModalMessage"
            @dismiss="messageModalMessage = null"
        />
        <Confirm v-if="showConfirmOne" @dismiss="showConfirmOne = false" @close="handleDeleteOne">
            <span slot="header">Supprimer l'emprise</span>
            <span slot="body"> Êtes-vous sûr de vouloir supprimer cette emprise ?<br /> </span>
        </Confirm>
        <Confirm
            v-if="showConfirmMany"
            @dismiss="showConfirmMany = false"
            @close="handleDeleteMany"
        >
            <span slot="header">
                Supprimer {{ selected.length }} {{ selected.length > 1 ? 'attributs' : 'attribut' }}
            </span>
            <span slot="body">
                Êtes-vous sûr de vouloir supprimer les emprises sélectionnés ?<br />
            </span>
        </Confirm>
        <CalculateConfirm
            v-if="showCalculateConfirm"
            @dismiss="showCalculateConfirm = false"
            @close="handleCalculateConfirm"
        >
            <span slot="header">Exécuter le calcul d'intersection avec l'emprise</span>
            <span slot="body"
                >Êtes-vous sûr de vouloir lancer le calcul de l'intersection<br />des objets OSM
                avec cette emprise ?</span
            >
        </CalculateConfirm>
        <h2 class="title">Emprises</h2>
        <LoadingDots v-if="isLoading" />
        <CardPager>
            <FootprintCard
                v-for="item in filtered"
                :item="item"
                :key="item.id"
                :handleCalculate="handleCalculate"
                :handleEdit="handleEdit"
                :handleConfirm="handleConfirm"
                :selected="selected"
                @selected="handleSelect"
            />
        </CardPager>
    </div>
</template>

<script>
import FilterView from '@/components/config/Filter';
import ModalFootprint from '@/components/config/ModalFootprint';
import ModalCalculateLoading from '@/components/config/ModalCalculateLoading';
import LoadingDots from '@/components/LoadingDots';
import FootprintCard from '@/components/config/FootprintCard';
import CardPager from '@/components/CardPager';
import CustomButton from '@/components/Button';
import Confirm from '@/components/config/DeleteConfirm';
import ModalMessage from '@/components/config/ModalMessage';
import CalculateConfirm from '@/components/config/CalculateConfirm';

export default {
    data() {
        return {
            showCalculateConfirm: false,
            showEdit: false,
            showConfirmOne: false,
            showConfirmMany: false,
            footprints: [],
            users: [],
            footprint: null,
            selected: [],
            searchTerm: null,
            isLoading: true,
            messageModalMessage: null,
            headerModalMessage: null,
            bodyModalMessage: null,
            errorModalMessage: null,
            footerModalMessage: null,
            loadingModalMessage: null,
        };
    },
    watch: {
        searchTerm() {
            this.selected.splice(0);
        },
    },
    computed: {
        filtered() {
            if (this.searchTerm) {
                const re = new RegExp(this.searchTerm, 'i');
                return this.footprints.filter((o) => re.test(o.name));
            }
            return this.footprints;
        },
    },
    mounted() {
        Promise.all([this.$http.get('/api/footprint'), this.$http.get('/api/user')]).then(
            (responses) => {
                // success callback
                this.footprints = responses[0].body;
                this.users = responses[1].body.map((u) => ({
                    id: u.id,
                    text: `${u.first_name} ${u.last_name}`,
                }));
                this.isLoading = false;
            },
            (responses) => {
                // error callback
                this.isLoading = false;
                console.error(responses);
            },
        );
    },
    methods: {
        _getFootprint(id) {
            const footprint = this.footprints.find((t) => t.id === id);
            return {
                ...footprint,
                users: footprint.users.map((u) => ({
                    id: u.id,
                    text: `${u.first_name} ${u.last_name}`,
                })),
            };
        },
        handleSelect(event) {
            const { id, checked } = event;
            const index = this.selected.findIndex((s) => s === id);
            if (checked) {
                if (index === -1) {
                    this.selected.push(id);
                }
            } else {
                if (index > -1) {
                    this.selected.splice(index, 1);
                }
            }
        },
        handleCalculate(item) {
            this.footprint = this._getFootprint(item);
            this.showCalculateConfirm = true;
        },
        handleConfirm(item) {
            this.footprint = this._getFootprint(item);
            this.showConfirmOne = true;
        },
        handleEdit(item) {
            this.footprint = this._getFootprint(item);
            this.showEdit = true;
        },
        handleClose() {
            // Crée la nouvelle emprise, si le nom de l'emprise est renseigné
            if (!this.footprint.id && this.footprint.name) {
                this.$http
                    .post('/api/footprint', this.footprint)
                    .then(() => {
                        this.showEdit = false;
                        this.footprint = null;
                        this.isLoading = true;
                        this.$http
                            .get('/api/footprint')
                            .then(
                                (response) => {
                                    // success callback
                                    this.footprints = response.body;
                                },
                                (response) => {
                                    // error callback
                                    console.error(response);
                                },
                            )
                            .finally(() => (this.isLoading = false));
                    })
                    .catch();
                // Met à jour l'emprise, si le nom de l'emprise est renseignée
            } else if (this.footprint.id && this.footprint.name) {
                this.$http
                    .put(`/api/footprint/${this.footprint.id}`, this.footprint)
                    .then((response) => {
                        const footprint = response.body;
                        const index = this.footprints.findIndex((t) => t.id === footprint.id);
                        this.footprints.splice(index, 1, footprint);
                        this.showEdit = false;
                        this.footprint = null;
                    })
                    .catch();
            }
        },
        handleNew() {
            this.footprint = {
                id: null,
                name: '',
                users: [],
            };
            this.showEdit = true;
        },
        handleDeleteOne() {
            this.$http
                .delete(`/api/footprint/${this.footprint.id}`)
                .then(() => {
                    const index = this.footprints.findIndex((t) => t.id === this.footprint.id);
                    this.footprints.splice(index, 1);
                    this.showConfirmOne = false;
                    this.footprint = null;
                })
                .catch((res) => {
                    if (res && res.body && res.body.error === 'Cannot delete default printfoot') {
                        this.loadingModalMessage = null;
                        this.messageModalMessage = 'Erreur';
                        this.headerModalMessage = `Erreur lors de la suppression de l'emprise`;
                        this.bodyModalMessage = null;
                        this.errorModalMessage = `L'emprise par défaut ne peut pas être supprimée.`;
                        this.footerModalMessage = null;
                        this.showConfirmOne = false;
                    }
                });
        },
        handleDeleteMany() {
            const requests = this.selected.map((s) => this.$http.delete(`/api/footprint/${s}`));
            Promise.all(requests)
                .then(() => {
                    this.showConfirmMany = false;
                    this.selected.splice(0);
                    this.isLoading = true;
                    return this.$http.get('/api/footprint');
                })
                .then((response) => {
                    // success callback
                    this.footprints = response.body;
                })
                .catch(() => {})
                .finally(() => (this.isLoading = false));
        },
        handleCalculateConfirm() {
            // Ici le code à exécuter pour le calcul des intersections avec l'emprise

            this.loadingModalMessage =
                'Calcul des intersections en cours…\nCela peut prendre plusieurs minutes.';
            this.showCalculateConfirm = false;

            if (this.footprint.id) {
                this.$http
                    .put(`/api/footprint/${this.footprint.id}/clean`, this.footprint)
                    .then(() => {
                        return this.$http.put(
                            `/api/footprint/${this.footprint.id}/calculate`,
                            this.footprint,
                        );
                    })
                    .then(() => {
                        this.showCalculateConfirm = false;
                        this.footprint = null;
                        this.loadingModalMessage = null;
                        this.messageModalMessage = 'Terminé';
                        this.headerModalMessage = 'Le calcul des intersections est terminé';
                        this.bodyModalMessage = null;
                        this.errorModalMessage = null;
                        this.footerModalMessage = null;
                    })
                    .catch((err) => {
                        this.loadingModalMessage = null;
                        this.messageModalMessage = 'Erreur';
                        this.headerModalMessage = 'Erreur lors du calcul des intersections';
                        this.bodyModalMessage = null;
                        this.errorModalMessage = `Statut : ${err.statusText}\nNom : ${
                            err.body.error.name
                        }\nCode : ${err.body.error.original.code}\nRoutine : ${
                            err.body.error.original.routine
                        }`;
                        this.footerModalMessage = null;
                    })
                    .finally(() => (this.isLoading = false));
            }
        },
    },
    components: {
        ModalFootprint,
        ModalCalculateLoading,
        FilterView,
        LoadingDots,
        FootprintCard,
        CardPager,
        CustomButton,
        Confirm,
        ModalMessage,
        CalculateConfirm,
    },
};
</script>

<style lang="scss"></style>
